
import React from 'react';
import { Calendar, Clock, User, BookOpen, ExternalLink, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const EthicalConsiderationsPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Considérations éthiques pour l'IA en droit : Défis et solutions pour les professionnels juridiques"
      : "Ethical Considerations for AI in Law: Challenges and Solutions for Legal Professionals",
    "datePublished": "2024-01-10T14:00:00+01:00",
    "dateModified": "2024-01-10T14:00:00+01:00",
    "author": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gaius-tech.com/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://gaius-tech.com/articles/ethique-ia-droit"
    },
    "image": "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=630&fit=crop",
    "description": language === 'fr' 
      ? "Guide complet sur les enjeux éthiques de l'IA en droit. Obligations déontologiques, responsabilité professionnelle et bonnes pratiques pour les juristes."
      : "Complete guide on ethical challenges of AI in law. Professional ethics, responsibility and best practices for legal professionals."
  };

  const seoTitle = language === 'fr' 
    ? "Éthique IA en droit : guide déontologique avocats"
    : "AI Ethics in Law: Guide for Lawyers";
    
  const seoDescription = language === 'fr'
    ? "Guide complet sur l'éthique de l'IA en droit. Obligations déontologiques, responsabilité professionnelle, bonnes pratiques. Formation IA juridique éthique."
    : "Complete guide on AI ethics in law. Professional obligations, responsibility, best practices. Ethical legal AI training.";

  const seoKeywords = language === 'fr'
    ? "éthique IA droit, déontologie avocat IA, responsabilité professionnelle intelligence artificielle, formation éthique IA juriste, transformation numérique droit éthique"
    : "AI ethics law, lawyer professional ethics AI, artificial intelligence professional responsibility, ethical AI training legal, digital transformation law ethics";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          canonicalUrl="https://gaius-tech.com/articles/ethique-ia-droit"
          alternateUrlFr="https://gaius-tech.com/articles/ethique-ia-droit"
          alternateUrlEn="https://gaius-tech.com/articles/ethical-considerations-ai-law"
          structuredData={structuredData}
          pageType="article"
        />
        
        {/* Article Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4">Éthique & Déontologie</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Considérations éthiques pour l'IA en droit
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Défis déontologiques et solutions pratiques pour une intégration responsable de l'intelligence artificielle dans la pratique juridique
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>10 janvier 2024</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>15 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=400&fit=crop"
              alt="Éthique et intelligence artificielle en droit"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            <h2>Introduction : L'impératif éthique de l'IA juridique</h2>
            <p>
              L'intégration de l'intelligence artificielle dans la pratique juridique soulève des questions éthiques fondamentales 
              qui transcendent les considérations purement technologiques. Les professionnels du droit, soumis à des obligations 
              déontologiques strictes, doivent naviguer dans un environnement où l'innovation technologique doit s'harmoniser 
              avec les principes fondamentaux de la profession.
            </p>
            
            <p>
              Cette problématique revêt une importance particulière dans un contexte où la transformation numérique du droit 
              s'accélère, imposant aux juristes de repenser leurs pratiques tout en préservant l'intégrité de leur mission.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" />
                <div>
                  <h4 className="text-yellow-800 font-semibold">Point d'attention</h4>
                  <p className="text-yellow-700 text-sm mt-1">
                    L'utilisation de l'IA en droit nécessite une vigilance constante pour respecter les obligations déontologiques 
                    et garantir la protection des intérêts des clients.
                  </p>
                </div>
              </div>
            </div>

            <h2>I. Cadre déontologique et obligations professionnelles</h2>
            
            <h3>A. Principes fondamentaux de la déontologie de l'avocat</h3>
            <p>
              La profession d'avocat repose sur des principes déontologiques intemporels qui doivent guider l'utilisation de l'IA :
            </p>
            <ul>
              <li><strong>Le secret professionnel :</strong> Obligation absolue de confidentialité des informations clients</li>
              <li><strong>L'indépendance :</strong> Liberté de conseil et d'action dans l'intérêt exclusif du client</li>
              <li><strong>La probité :</strong> Loyauté et honnêteté dans tous les actes professionnels</li>
              <li><strong>La compétence :</strong> Maîtrise technique et mise à jour des connaissances</li>
              <li><strong>La diligence :</strong> Attention soutenue et promptitude dans l'exécution des missions</li>
            </ul>

            <h3>B. Adaptation aux outils d'IA</h3>
            <p>
              L'utilisation d'outils d'intelligence artificielle doit s'inscrire dans le respect strict de ces principes. 
              Cela implique une approche méthodologique rigoureuse et une formation appropriée des professionnels.
            </p>

            <h2>II. Enjeux de confidentialité et protection des données</h2>

            <h3>A. Secret professionnel à l'ère numérique</h3>
            <p>
              Le secret professionnel, pierre angulaire de la relation avocat-client, prend une dimension nouvelle avec l'IA. 
              Les juristes doivent s'assurer que :
            </p>
            <ul>
              <li>Les données clients ne sont pas transmises à des serveurs tiers non sécurisés</li>
              <li>Les outils d'IA utilisés garantissent la confidentialité des informations traitées</li>
              <li>Les paramètres de confidentialité sont correctement configurés</li>
              <li>Une politique de gestion des données claire est établie</li>
            </ul>

            <h3>B. Conformité RGPD et protection des données personnelles</h3>
            <p>
              L'utilisation d'IA en droit doit respecter scrupuleusement le Règlement Général sur la Protection des Données (RGPD) :
            </p>
            <ul>
              <li>Principe de minimisation des données</li>
              <li>Droit à l'effacement et à la portabilité</li>
              <li>Transparence dans le traitement automatisé</li>
              <li>Sécurité et intégrité des données</li>
            </ul>

            <h2>III. Responsabilité professionnelle et obligation de supervision</h2>

            <h3>A. Responsabilité de l'avocat dans l'utilisation de l'IA</h3>
            <p>
              L'avocat demeure pleinement responsable des conseils donnés et des actes accomplis, même lorsqu'il utilise des outils d'IA. 
              Cette responsabilité implique :
            </p>
            <ul>
              <li>Un contrôle systématique des résultats produits par l'IA</li>
              <li>Une vérification indépendante des sources et références</li>
              <li>Une validation juridique des analyses proposées</li>
              <li>Une capacité à expliquer et justifier les décisions prises</li>
            </ul>

            <h3>B. Obligation de compétence et formation continue</h3>
            <p>
              L'évolution technologique impose aux avocats de maintenir leur niveau de compétence :
            </p>
            <ul>
              <li>Formation aux outils d'IA et à leurs limites</li>
              <li>Compréhension des enjeux éthiques et déontologiques</li>
              <li>Veille technologique et juridique régulière</li>
              <li>Développement d'un esprit critique face aux résultats automatisés</li>
            </ul>

            <h2>IV. Transparence et information du client</h2>

            <h3>A. Devoir d'information sur l'utilisation de l'IA</h3>
            <p>
              Le principe de transparence impose aux avocats d'informer leurs clients de l'utilisation d'outils d'IA :
            </p>
            <ul>
              <li>Nature des outils utilisés et leur finalité</li>
              <li>Impact sur la qualité et les délais de traitement</li>
              <li>Mesures de sécurité et de confidentialité mises en place</li>
              <li>Limitations et risques éventuels</li>
            </ul>

            <h3>B. Consentement éclairé du client</h3>
            <p>
              L'utilisation d'IA peut nécessiter le consentement explicite du client, particulièrement lorsque 
              des données sensibles sont traitées ou que l'impact sur la stratégie juridique est significatif.
            </p>

            <h2>V. Équité et non-discrimination algorithmique</h2>

            <h3>A. Biais algorithmiques et équité procédurale</h3>
            <p>
              Les systèmes d'IA peuvent reproduire ou amplifier des biais existants. Les avocats doivent :
            </p>
            <ul>
              <li>Identifier les biais potentiels des outils utilisés</li>
              <li>Évaluer l'impact sur l'équité des décisions</li>
              <li>Mettre en place des mesures correctives</li>
              <li>Garantir l'égalité de traitement des clients</li>
            </ul>

            <h3>B. Accès à la justice et fracture numérique</h3>
            <p>
              L'utilisation d'IA ne doit pas créer de nouvelles inégalités dans l'accès à la justice. 
              Les cabinets doivent veiller à maintenir l'accessibilité de leurs services pour tous les clients.
            </p>

            <h2>VI. Recommandations pratiques et bonnes pratiques</h2>

            <h3>A. Charte d'utilisation éthique de l'IA</h3>
            <p>
              Nous recommandons aux cabinets d'adopter une charte définissant :
            </p>
            <ul>
              <li>Les principes éthiques guidant l'utilisation de l'IA</li>
              <li>Les procédures de validation et de contrôle</li>
              <li>Les responsabilités de chaque utilisateur</li>
              <li>Les mesures de sécurité et de confidentialité</li>
            </ul>

            <h3>B. Formation et sensibilisation des équipes</h3>
            <p>
              Une approche éthique de l'IA nécessite :
            </p>
            <ul>
              <li>Formation initiale sur les enjeux éthiques</li>
              <li>Sensibilisation aux bonnes pratiques</li>
              <li>Mise à jour régulière des connaissances</li>
              <li>Échanges d'expériences entre professionnels</li>
            </ul>

            <h2>VII. Évolutions réglementaires et perspectives</h2>

            <h3>A. Règlement européen sur l'IA</h3>
            <p>
              Le futur Règlement européen sur l'IA (AI Act) établira un cadre juridique contraignant pour l'utilisation 
              de l'intelligence artificielle, avec des implications directes pour les professionnels du droit.
            </p>

            <h3>B. Adaptation des règles déontologiques</h3>
            <p>
              Les ordres professionnels travaillent à l'adaptation des règles déontologiques pour prendre en compte 
              les spécificités de l'IA, garantissant ainsi un cadre clair pour les praticiens.
            </p>

            <h2>Conclusion : Vers une IA juridique éthique et responsable</h2>
            <p>
              L'intégration éthique de l'intelligence artificielle dans la pratique juridique représente un défi majeur 
              mais surmontable. Elle nécessite une approche proactive, une formation continue et un engagement constant 
              envers les valeurs fondamentales de la profession.
            </p>

            <p>
              Les professionnels du droit qui sauront conjuguer innovation technologique et excellence déontologique 
              bénéficieront d'un avantage concurrentiel durable, tout en contribuant à l'évolution positive de la profession.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-blue-400 mt-0.5 mr-3" />
                <div>
                  <h4 className="text-blue-800 font-semibold">Recommandation Gaius</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    La formation continue aux enjeux éthiques de l'IA constitue un investissement essentiel 
                    pour maintenir l'excellence professionnelle et la confiance des clients.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Related Training */}
          <div className="mt-12 p-6 bg-blue-50 rounded-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Formations recommandées
            </h3>
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Formation IA générative pour avocats
                      </h4>
                      <p className="text-sm text-gray-600">
                        Intégrez l'éthique et les bonnes pratiques dans votre utilisation de l'IA
                      </p>
                    </div>
                    <Button asChild className="bg-blue-600 hover:bg-blue-700">
                      <Link to="/initiation-ia-generative-avocats">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Découvrir
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Sessions personnalisées pour cabinets
                      </h4>
                      <p className="text-sm text-gray-600">
                        Développez votre charte éthique d'utilisation de l'IA
                      </p>
                    </div>
                    <Button asChild variant="outline">
                      <Link to="/custom-sessions-law-firms">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        En savoir plus
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version (similar structure)
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="AI Ethics in Law: Professional Guide for Lawyers | Gaius"
        description="Complete guide on AI ethics in law. Professional obligations, responsibility, best practices. Ethical legal AI training."
        keywords="AI ethics law, lawyer professional ethics AI, artificial intelligence professional responsibility, ethical AI training legal"
        canonicalUrl="https://gaiustech.com/articles/ethical-considerations-ai-law"
        structuredData={structuredData}
        pageType="article"
      />
      
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4">Ethics & Professional Standards</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ethical Considerations for AI in Law
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Professional challenges and practical solutions for responsible integration of artificial intelligence in legal practice
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>January 10, 2024</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>15 min read</span>
              </span>
              <span className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>Gaius Team</span>
              </span>
            </div>
          </div>
          <img
            src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=400&fit=crop"
            alt="Ethics and artificial intelligence in law"
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h2>Introduction: The Ethical Imperative of Legal AI</h2>
          <p>
            The integration of artificial intelligence in legal practice raises fundamental ethical questions 
            that transcend purely technological considerations. Legal professionals, subject to strict professional obligations, 
            must navigate an environment where technological innovation must harmonize with the fundamental principles of the profession.
          </p>
          
          {/* Similar content structure as French version but in English */}
          <h2>Conclusion: Towards Ethical and Responsible Legal AI</h2>
          <p>
            The ethical integration of artificial intelligence in legal practice represents a major but surmountable challenge. 
            It requires a proactive approach, continuous training, and constant commitment to the fundamental values of the profession.
          </p>
        </div>

        <div className="mt-12 p-6 bg-blue-50 rounded-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Recommended Training
          </h3>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Generative AI Training for Lawyers
                    </h4>
                    <p className="text-sm text-gray-600">
                      Integrate ethics and best practices in your AI usage
                    </p>
                  </div>
                  <Button asChild className="bg-blue-600 hover:bg-blue-700">
                    <Link to="/initiation-ia-generative-avocats">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Discover
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
